import { useState } from 'react'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import { TopDiscountsTodayBaseProps, calculateDiscount, formatDiscount, formatPrice } from './TopDiscountsTodayBase'
import { TagBadge } from '@/components/ui/badge'
import { PriceChart } from './PriceChart'
import { ProjectLogo } from '../UpcomingUnlocks/ProjectLogo'

export const TopDiscountsTodayDesktop = ({ items, loading = false, onItemClick, onBuyClick }: TopDiscountsTodayBaseProps) => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const itemsPerPage = 3

  const handlePrev = () => {
    setCurrentIndex((prev) => Math.max(0, prev - 1))
  }

  const handleNext = () => {
    setCurrentIndex((prev) => Math.min(items.length - itemsPerPage, prev + 1))
  }

  const visibleItems = items.slice(currentIndex, currentIndex + itemsPerPage)

  // Show loading state
  if (loading) {
    return (
      <div className="h-full flex flex-col">
        <h3 
          className="text-base leading-6 text-[#2d2923] dark:text-[#fdfdfd] font-medium uppercase mb-3" 
          
        >
          TOP DISCOUNTS TODAY
        </h3>
        <div className="flex-1 flex items-center justify-center">
          <p className="text-sm text-muted-foreground dark:text-[rgba(253,253,253,0.72)]">Loading discounts...</p>
        </div>
      </div>
    )
  }

  // Show empty state
  if (items.length === 0) {
    return (
      <div className="h-full flex flex-col">
        <h3 
          className="text-base leading-6 text-[#2d2923] dark:text-[#fdfdfd] font-medium uppercase mb-3" 
          
        >
          TOP DISCOUNTS TODAY
        </h3>
        <div className="flex-1 flex items-center justify-center">
          <p className="text-sm text-muted-foreground dark:text-[rgba(253,253,253,0.72)]">No discounted assets available</p>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h3 
          className="text-base leading-6 text-[#2d2923] dark:text-[#fdfdfd] font-medium uppercase" 
          
        >
          TOP DISCOUNTS TODAY
        </h3>
        
        {/* Navigation arrows */}
        <div className="flex items-center gap-2">
          <button
            onClick={handlePrev}
            disabled={currentIndex === 0}
            className="w-6 h-6 flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Previous"
          >
            <ArrowLeft className={`w-6 h-6 ${currentIndex === 0 ? 'text-gray-300 dark:text-[rgba(253,253,253,0.3)]' : 'text-[#2d2923] dark:text-[#fdfdfd]'}`} strokeWidth={1.5} />
          </button>
          <button
            onClick={handleNext}
            disabled={currentIndex >= items.length - itemsPerPage}
            className="w-6 h-6 flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Next"
          >
            <ArrowRight className={`w-6 h-6 ${currentIndex >= items.length - itemsPerPage ? 'text-gray-300 dark:text-[rgba(253,253,253,0.3)]' : 'text-[#2d2923] dark:text-[#fdfdfd]'}`} strokeWidth={1.5} />
          </button>
        </div>
      </div>

      {/* Cards container */}
      <div className="flex gap-3 overflow-x-auto flex-1 scrollbar-hide">
        {visibleItems.map((item, index) => {
          const discount = calculateDiscount(item)
          return (
            <div
              key={item.order_id}
              className="flex-shrink-0 w-[270px] flex flex-col cursor-pointer"
              onClick={() => onItemClick?.(item)}
            >
              {/* Card content */}
              <div className="bg-white dark:bg-[#1c1c1c] flex flex-col gap-2.5 p-3">
                {/* Header with name and badge */}
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <ProjectLogo logo={item.logo} alt={item.project_name || ''} size="sm" />
                    <h4 
                      className="text-sm leading-5 text-[#2d2923] dark:text-[#fdfdfd] font-semibold" 
                    >
                      {item.project_name}
                    </h4>
                  </div>
                  <TagBadge tag={item.tag} />
                </div>

                {/* Discount percentage and price */}
                <div className="flex flex-col gap-0.5 -mt-1">
                  <p 
                    className="leading-6 text-[#2d2923] dark:text-[#fdfdfd] tracking-[-0.2px] font-medium" 
                  >
                    <span className="text-[20px]">-{formatDiscount(discount)} </span>
                    <span className="text-[13px] text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)]">%</span>
                    <span className="text-[11px] text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)] ml-1 font-normal">from last round</span>
                  </p>
                  <p 
                    className="text-[11px] leading-[14px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]" 
                  >
                    {formatPrice(item)}
                  </p>
                </div>

                {/* Buttons */}
                <div className="flex gap-1.5">
                  <button
                    className="bg-[#2d2923] dark:bg-[#fdfdfd] flex-1 flex items-center justify-center px-3 py-2 text-[10px] leading-3 text-white dark:text-[#2d2923] font-medium hover:bg-[#1a1612] dark:hover:bg-[rgba(253,253,253,0.9)] transition-colors"
                    onClick={(e) => {
                      e.stopPropagation()
                      onBuyClick?.(item)
                    }}
                  >
                    BUY
                  </button>
                  <button
                    className="bg-white dark:bg-transparent border border-[rgba(45,41,35,0.12)] dark:border-[rgba(253,253,253,0.12)] flex-1 flex items-center justify-center px-3 py-2 text-[10px] leading-3 text-[#2d2923] dark:text-[#fdfdfd] font-medium hover:bg-gray-50 dark:hover:bg-[rgba(253,253,253,0.1)] transition-colors"
                    onClick={(e) => {
                      e.stopPropagation()
                      onItemClick?.(item)
                    }}
                  >
                    More details
                  </button>
                </div>
              </div>

              {/* Chart section */}
              <div className="bg-[rgba(255,255,255,0.48)] dark:bg-[rgba(28,28,28,0.48)] p-3">
                <PriceChart data={item.priceHistory} seed={index + (item.order_id?.charCodeAt(0) || 0)} />
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

